/**
 * Live Screen Composition Planner stages (Phase A–E) for the right panel.
 */
import React, { useMemo } from 'react';
import { Check, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  SCREEN_COMPOSITION_STAGES,
  compositionStageFromJob,
  stageIndex,
} from '@/lib/reelsShortsCompositionStages';

export function ReelsShortsCompositionStages({
  job = null,
  active = false,
  complete = false,
}) {
  const live = useMemo(() => compositionStageFromJob(job), [job]);
  const running = ['pending', 'running'].includes(String(job?.status || '').toLowerCase());
  const show = active || running || complete || Boolean(live);
  if (!show) return null;

  const currentPhase = complete && !running ? 'done' : (live?.phase || 'prep');
  const currentIdx = stageIndex(currentPhase);
  const currentStep = live?.step || '';

  return (
    <div
      className="rounded-xl border border-rose-400/25 bg-rose-500/[0.06] p-3 space-y-3"
      data-testid="reels-composition-stages"
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="text-xs font-semibold text-white">Screen composition</p>
          <p className="text-[10px] text-[#9CA3AF] mt-0.5">
            {running
              ? (live?.detail || live?.stepLabel || job?.message || 'Running planner…')
              : complete
                ? 'All phases finished for this export'
                : 'Phases A–E run when you export'}
          </p>
        </div>
        {running ? (
          <Loader2 className="w-3.5 h-3.5 text-rose-300 animate-spin shrink-0 mt-0.5" />
        ) : null}
      </div>

      {live?.format || live?.formatsTotal ? (
        <p className="text-[10px] text-rose-100/80" data-testid="reels-composition-stage-format">
          {live.format ? `Format ${live.format}` : null}
          {live.formatsTotal
            ? `${live.format ? ' · ' : ''}${live.formatsDone?.length || 0}/${live.formatsTotal} targets`
            : null}
        </p>
      ) : null}

      <ol className="space-y-1.5">
        {SCREEN_COMPOSITION_STAGES.map((phase, idx) => {
          const done = idx < currentIdx || (complete && !running && idx <= currentIdx);
          const current = idx === currentIdx && running;
          return (
            <li
              key={phase.id}
              className={cn(
                'rounded-lg border px-2.5 py-2',
                current && 'border-rose-400/50 bg-rose-500/15',
                done && !current && 'border-emerald-500/25 bg-emerald-500/5',
                !done && !current && 'border-white/5 bg-black/20',
              )}
              data-testid={`reels-composition-phase-${phase.id}`}
              data-state={current ? 'active' : done ? 'done' : 'pending'}
            >
              <div className="flex items-center gap-2">
                <span
                  className={cn(
                    'w-5 h-5 rounded-full text-[10px] font-bold flex items-center justify-center shrink-0',
                    current && 'bg-rose-400 text-black',
                    done && !current && 'bg-emerald-400 text-black',
                    !done && !current && 'bg-[#1a202c] text-slate-500',
                  )}
                >
                  {done && !current ? <Check className="w-3 h-3" /> : phase.letter}
                </span>
                <div className="min-w-0 flex-1">
                  <p className={cn('text-[11px] font-semibold truncate', current ? 'text-white' : 'text-slate-200')}>
                    {phase.label}
                  </p>
                  <p className="text-[9px] text-[#6B7280] truncate">
                    {current && live?.stepLabel ? live.stepLabel : phase.hint}
                  </p>
                </div>
              </div>

              {(current || (done && phase.id === currentPhase)) && phase.steps?.length > 1 ? (
                <ul className="mt-1.5 ml-7 space-y-0.5" data-testid={`reels-composition-steps-${phase.id}`}>
                  {phase.steps.map((step) => {
                    const stepIds = phase.steps.map((s) => s.id);
                    const stepIdx = stepIds.indexOf(step.id);
                    const curStepIdx = stepIds.indexOf(currentStep);
                    const stepDone = done
                      || (current && curStepIdx >= 0 && stepIdx < curStepIdx)
                      || (current && currentStep === step.id && !running);
                    const stepCurrent = current && currentStep === step.id && running;
                    return (
                      <li
                        key={step.id}
                        className={cn(
                          'text-[9px] flex items-center gap-1.5',
                          stepCurrent && 'text-rose-100',
                          stepDone && !stepCurrent && 'text-emerald-300/80',
                          !stepDone && !stepCurrent && 'text-[#6B7280]',
                        )}
                      >
                        <span
                          className={cn(
                            'w-1.5 h-1.5 rounded-full shrink-0',
                            stepCurrent && 'bg-rose-300 animate-pulse',
                            stepDone && !stepCurrent && 'bg-emerald-400',
                            !stepDone && !stepCurrent && 'bg-white/15',
                          )}
                        />
                        {step.label}
                      </li>
                    );
                  })}
                </ul>
              ) : null}
            </li>
          );
        })}
      </ol>
    </div>
  );
}

export default ReelsShortsCompositionStages;
